import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { X, Images } from "lucide-react";

import AlbumGallerySlider from "./AlbumGallerySlider";

import "./AlbumGalleryModal.css";

export default function AlbumGalleryModal({
  open,
  images = [],
  initialIndex = 0,
  title = "Album Gallery",
  description = "",
  brandName = "",
  username = "",
  avatarUrl = null,
  onClose,
}) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  const total = images.length;
  const index = Math.min(Math.max(activeIndex, 0), Math.max(total - 1, 0));
  const currentImage = images[index];

  useEffect(() => {
    if (open) {
      setActiveIndex(initialIndex);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;

    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      ).filter((element) => element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);

      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const channelName = brandName || username || "Katch";

  const initials = channelName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();

  const channelUrl = username ? `/${encodeURIComponent(username)}` : null;

  return createPortal(
    <div className="album-gallery-modal">
      <div
        ref={dialogRef}
        className="album-gallery-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="album-gallery-modal__viewer">
          <button
            ref={closeButtonRef}
            type="button"
            className="album-gallery-modal__close"
            onClick={onClose}
            aria-label="Close gallery"
          >
            <X size={23} />
          </button>

          <AlbumGallerySlider
            images={images}
            activeIndex={index}
            onIndexChange={setActiveIndex}
          />
        </div>

        <aside className="album-gallery-modal__sidebar">
          {channelUrl && (
            <Link to={channelUrl} className="album-gallery-modal__channel">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="album-gallery-modal__channel-avatar"
                />
              ) : (
                <span className="album-gallery-modal__channel-placeholder">
                  {initials}
                </span>
              )}

              <span className="album-gallery-modal__channel-info">
                <strong>{channelName}</strong>
                <small>@{username}</small>
              </span>
            </Link>
          )}

          <div className="album-gallery-modal__sidebar-header">
            <div className="album-gallery-modal__album-info">
              <h2>{title}</h2>

              {description && (
                <p className="album-gallery-modal__description">
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="album-gallery-modal__photo-details">
            <span className="album-gallery-modal__label">Photos</span>

            <strong>{total ? `${index + 1} of ${total}` : "No photos"}</strong>
          </div>

          {(currentImage?.title || currentImage?.description) && (
            <div className="album-gallery-modal__caption">
              {currentImage.title && <h3>{currentImage.title}</h3>}

              {currentImage.description && <p>{currentImage.description}</p>}
            </div>
          )}
        </aside>
      </div>
    </div>,
    document.body,
  );
}
